'use client'

import { useState } from 'react'
import { Button } from '@/ui/button'
import { Badge } from '@/ui/badge'
import {
  CheckCircleIcon,
  XCircleIcon,
  WarningIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react'
import { validateMigration, type MigrationValidationResult } from './validation'
import { useDocumentView, useDocumentStatus } from '../use-document'

export function MigrationValidationStatus() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()
  const [validationResult, setValidationResult] =
    useState<MigrationValidationResult | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  const runValidation = async () => {
    if (!doc) return

    setIsValidating(true)
    try {
      // Small delay to show loading state
      await new Promise((resolve) => setTimeout(resolve, 100))
      const result = validateMigration(doc)
      setValidationResult(result)
    } finally {
      setIsValidating(false)
    }
  }

  if (!ready) {
    return (
      <div className="rounded-lg border p-4">
        <p className="text-muted-foreground text-sm">Loading document...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Data Validation</h3>
          <p className="text-muted-foreground text-sm">
            Verify data integrity after migration
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={runValidation}
          disabled={isValidating}
        >
          {isValidating ? (
            <ArrowsClockwiseIcon className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ArrowsClockwiseIcon className="mr-2 h-4 w-4" />
          )}
          {isValidating ? 'Validating...' : 'Run Validation'}
        </Button>
      </div>

      {validationResult && (
        <div className="space-y-3">
          {/* Summary */}
          <div className="flex items-center gap-2">
            {validationResult.valid ? (
              <>
                <CheckCircleIcon
                  className="text-success h-5 w-5"
                  weight="fill"
                />
                <span className="text-success font-medium">
                  All validations passed
                </span>
              </>
            ) : (
              <>
                <XCircleIcon
                  className="text-destructive h-5 w-5"
                  weight="fill"
                />
                <span className="text-destructive font-medium">
                  {validationResult.summary.errorCount} errors found
                </span>
              </>
            )}
            {validationResult.summary.warningCount > 0 && (
              <Badge variant="secondary">
                {validationResult.summary.warningCount} warnings
              </Badge>
            )}
          </div>

          {/* Stats */}
          <div className="text-muted-foreground flex gap-4 text-sm">
            <span>{validationResult.summary.totalEntries} entries</span>
            <span>{validationResult.summary.totalTopics} topics</span>
            <span>{validationResult.summary.totalProjects} projects</span>
          </div>

          {/* Phase details */}
          <div className="space-y-2">
            {Object.entries(validationResult.phases).map(([phase, result]) => (
              <div key={phase} className="flex items-center gap-2 text-sm">
                {result.valid ? (
                  <CheckCircleIcon className="text-success h-4 w-4" />
                ) : (
                  <XCircleIcon className="text-destructive h-4 w-4" />
                )}
                <span className="capitalize">{phase}</span>
                {result.errors.length > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {result.errors.length} errors
                  </Badge>
                )}
                {result.warnings.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {result.warnings.length} warnings
                  </Badge>
                )}
              </div>
            ))}
          </div>

          {/* Error details */}
          {validationResult.summary.errorCount > 0 && (
            <details className="mt-4">
              <summary className="text-destructive cursor-pointer text-sm font-medium">
                View errors
              </summary>
              <ul className="mt-2 space-y-1 text-sm">
                {Object.values(validationResult.phases).flatMap((p) =>
                  p.errors.map((err, i) => (
                    <li key={`${p.phase}-${i}`} className="text-destructive">
                      [{err.entityType || p.phase}] {err.message}
                    </li>
                  )),
                )}
              </ul>
            </details>
          )}

          {/* WarningIcon details */}
          {validationResult.summary.warningCount > 0 && (
            <details className="mt-2">
              <summary className="text-muted-foreground cursor-pointer text-sm font-medium">
                View warnings
              </summary>
              <ul className="mt-2 space-y-1 text-sm">
                {Object.values(validationResult.phases).flatMap((p) =>
                  p.warnings.map((warn, i) => (
                    <li key={`${p.phase}-warn-${i}`} className="text-warning">
                      <WarningIcon className="mr-1 inline h-3 w-3" />
                      {warn.message}
                    </li>
                  )),
                )}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  )
}
